"use client";

import universalBtnBg from "@/_assets/images/Universal btn bg.png";
import { useTranslations } from "next-intl";
import Image from "next/image";

export type PlanType = "standard" | "universal";

function PlanOptionButton({
  value,
  selected,
  onSelect,
  title,
  description,
  disabled = false,
}: {
  value: PlanType;
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  disabled?: boolean;
}) {
  const isUniversal = value === "universal";

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onSelect}
      className={`relative flex min-h-[38px] w-full min-w-0 flex-1 flex-col justify-center gap-0.5 overflow-hidden rounded-[8px] px-2.5 text-left transition-colors sm:min-h-[55px] sm:gap-1 sm:px-3 xl:h-11 xl:min-h-0 xl:w-[230px] xl:max-w-[230px] xl:flex-none xl:gap-0 xl:py-0 ${
        disabled
          ? "cursor-not-allowed border border-border bg-muted opacity-50"
          : isUniversal
            ? "border-l-[9.80224609375px] border-l-primary text-white"
            : selected
              ? "border border-primary bg-primary/5 shadow-sm"
              : "border border-border bg-muted"
      }`}
    >
      {isUniversal && !disabled && (
        <>
          <Image
            src={universalBtnBg}
            alt=""
            fill
            sizes="171px"
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/55 to-primary/50" />
          <div className="absolute -right-5 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full bg-primary/60 blur-xl" />
        </>
      )}

      <span className="relative z-10 flex items-center justify-between gap-2">
        <span
          className={`truncate text-sm font-600 ${
            disabled
              ? "text-muted-foreground"
              : isUniversal
                ? ""
                : "text-foreground"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
            disabled
              ? "border-muted-foreground/30"
              : selected
                ? isUniversal
                  ? "border-white"
                  : "border-primary"
                : isUniversal
                  ? "border-white/40"
                  : "border-muted-foreground/30"
          }`}
        >
          {selected && !disabled && (
            <span
              className={`block h-2 w-2 rounded-full ${isUniversal ? "bg-white" : "bg-primary"}`}
            />
          )}
        </span>
      </span>
      <span
        className={`relative z-10 hidden truncate text-[13px] leading-tight sm:block xl:text-[12px] xl:leading-none ${
          disabled
            ? "text-muted-foreground"
            : isUniversal
              ? "text-white"
              : "text-gray-600"
        }`}
      >
        {description}
      </span>
    </button>
  );
}

function PlanTypeToggle({
  planType,
  setPlanType,
  disableUniversal = false,
}: {
  planType: PlanType;
  setPlanType: (type: PlanType) => void;
  disableUniversal?: boolean;
}) {
  const t = useTranslations("Shared.countryPackages.planTypeToggle");

  return (
    <div className="flex w-full flex-row gap-2 sm:gap-3 xl:w-auto">
      <PlanOptionButton
        value="standard"
        selected={planType === "standard"}
        onSelect={() => setPlanType("standard")}
        title={t("standard.title")}
        description={t("standard.description")}
      />
      <PlanOptionButton
        value="universal"
        selected={planType === "universal" && !disableUniversal}
        onSelect={() => setPlanType("universal")}
        title={t("universal.title")}
        description={t("universal.description")}
        disabled={disableUniversal}
      />
    </div>
  );
}

export default PlanTypeToggle;
